import { initDeferredNavbarImages } from "../navbar-deferred-images.js";

const NAVBAR_DROPDOWN_SETTINGS = {
  openDelay: 120,
  closeDelay: 220,
  transitionDuration: 260,
  mobileBreakpoint: 991,
  heightVariable: "--_sizes---elements--nav",
};

const MOBILE_NAV_THEME_BREAKPOINT = 767;
const NAVBAR_SEARCH_PHONE_BREAKPOINT = 767;
const MOBILE_NAV_THEME_LEAD_RATIO = 0.15;
const NAV_THEME_TRIGGER_RATIO = 0.5;
const LOCALISATION_PANEL_DISPLAY = "flex";

function getMediaQuery(query) {
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(query);
  }

  return {
    matches: false,
    addEventListener() {},
  };
}

function runWhenDocumentReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

export function initNavbarDropdowns() {
  const navbar = document.querySelector(".navbar");
  const dropdownWrappers = Array.from(document.querySelectorAll(".navbar__dropdown-wrap"));
  if (!navbar || !dropdownWrappers.length) return;

  const overlay = document.querySelector(".navbar__overlay");
  const overlayClose = document.querySelector(".navbar__overlay-close");
  const dropdownPanels = new Map(
    dropdownWrappers.map((dropdownWrapper) => [
      dropdownWrapper,
      dropdownWrapper.querySelector(".navbar__dropdown"),
    ])
  );
  const mobileQuery = getMediaQuery(`(max-width: ${NAVBAR_DROPDOWN_SETTINGS.mobileBreakpoint}px)`);
  const timers = {
    open: null,
    close: null,
    settle: null,
    overlay: null,
  };

  let currentDropdown = null;
  let lastMobileState = null;
  let overlayAnimationFrame = null;
  let navbarHeightMeasure = null;

  const isMobile = () => mobileQuery.matches;
  const getDropdownHeight = (dropdownWrapper) =>
    dropdownPanels.get(dropdownWrapper)?.scrollHeight || 0;

  const clearTimers = () => {
    Object.values(timers).forEach(clearTimeout);
  };

  const getNavbarHeight = () => {
    if (!navbarHeightMeasure) {
      navbarHeightMeasure = document.createElement("div");
      Object.assign(navbarHeightMeasure.style, {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        width: "0",
        height: `var(${NAVBAR_DROPDOWN_SETTINGS.heightVariable})`,
      });
      navbar.appendChild(navbarHeightMeasure);
    }

    return (
      navbarHeightMeasure.getBoundingClientRect().height || navbarHeightMeasure.offsetHeight || 0
    );
  };

  // The navbar search preview drives the same overlay while it is open. Without this guard
  // a dropdown close (or an outside click) would reset the height and `is-active` out from
  // under the visible search results.
  const isSearchActive = () => navbar.classList.contains("search-active");

  const setOverlayHeight = (panelHeight) => {
    if (!overlay || isSearchActive()) return;

    const navbarHeight = panelHeight > 0 ? getNavbarHeight() : 0;
    window.cancelAnimationFrame?.(overlayAnimationFrame);
    overlayAnimationFrame = window.requestAnimationFrame(() => {
      overlay.style.height = `${Math.max(0, panelHeight + navbarHeight)}px`;
    });
  };

  const setBackdropActive = (isActive) => {
    if (isSearchActive()) return;

    overlay?.classList.toggle("is-active", isActive);
    overlayClose?.classList.toggle("is-active", isActive);
    navbar.classList.toggle("is-active", isActive);
  };

  const openDropdown = (dropdownWrapper) => {
    if (isMobile() || !dropdownWrapper) return;

    clearTimeout(timers.settle);
    clearTimeout(timers.overlay);

    const previousDropdown = currentDropdown;
    const nextHeight = getDropdownHeight(dropdownWrapper);
    const previousHeight = previousDropdown ? getDropdownHeight(previousDropdown) : 0;

    setBackdropActive(true);

    if (previousDropdown && previousDropdown !== dropdownWrapper) {
      previousDropdown.classList.remove("is-open");
      dropdownWrapper.classList.add("is-open");
      currentDropdown = dropdownWrapper;
      setOverlayHeight(Math.max(nextHeight, previousHeight));

      if (nextHeight < previousHeight) {
        timers.settle = setTimeout(() => {
          if (currentDropdown === dropdownWrapper) setOverlayHeight(nextHeight);
        }, NAVBAR_DROPDOWN_SETTINGS.transitionDuration);
      }
      return;
    }

    currentDropdown = dropdownWrapper;
    window.requestAnimationFrame(() => {
      dropdownWrapper.classList.add("is-open");
      setOverlayHeight(nextHeight);
    });
  };

  const closeDropdown = () => {
    if (isMobile()) return;

    clearTimeout(timers.settle);
    clearTimeout(timers.overlay);

    if (!currentDropdown) {
      setBackdropActive(false);
      setOverlayHeight(0);
      return;
    }

    const closingDropdown = currentDropdown;
    setOverlayHeight(getDropdownHeight(closingDropdown));
    closingDropdown.classList.remove("is-open");
    currentDropdown = null;

    timers.overlay = setTimeout(() => {
      setOverlayHeight(0);
      setBackdropActive(false);
    }, NAVBAR_DROPDOWN_SETTINGS.transitionDuration);
  };

  const scheduleDropdownOpen = (dropdownWrapper) => {
    clearTimeout(timers.open);
    timers.open = setTimeout(
      () => openDropdown(dropdownWrapper),
      NAVBAR_DROPDOWN_SETTINGS.openDelay
    );
  };

  const scheduleDropdownClose = () => {
    clearTimeout(timers.close);
    timers.close = setTimeout(closeDropdown, NAVBAR_DROPDOWN_SETTINGS.closeDelay);
  };

  dropdownWrappers.forEach((dropdownWrapper) => {
    dropdownWrapper.addEventListener("mouseenter", (event) => {
      if (isMobile()) return;
      clearTimeout(timers.close);
      scheduleDropdownOpen(event.currentTarget);
    });
    dropdownWrapper.addEventListener("mouseleave", () => {
      if (!isMobile()) scheduleDropdownClose();
    });

    const dropdownPanel = dropdownPanels.get(dropdownWrapper);
    dropdownPanel?.addEventListener("mouseenter", () => {
      if (!isMobile()) clearTimeout(timers.close);
    });
    dropdownPanel?.addEventListener("mouseleave", () => {
      if (!isMobile()) scheduleDropdownClose();
    });
  });

  navbar.addEventListener("mouseleave", () => {
    if (!isMobile()) scheduleDropdownClose();
  });

  [overlay, overlayClose].forEach((element) => {
    element?.addEventListener("click", () => {
      clearTimers();
      closeDropdown();
    });
  });

  const syncMobileNavbarState = () => {
    const hasActiveDropdown = document.querySelector(".navbar__dropdown-wrap.is-active");
    navbar.classList.toggle("is-active", Boolean(hasActiveDropdown));
  };

  document.addEventListener("click", (event) => {
    if (!isMobile()) return;

    const dropdownWrapper = event.target.closest(".navbar__dropdown-wrap");
    if (!dropdownWrapper || event.target.closest(".navbar__dropdown")) return;

    const shouldOpen = !dropdownWrapper.classList.contains("is-active");
    dropdownWrappers.forEach((element) => element.classList.remove("is-active"));
    dropdownWrapper.classList.toggle("is-active", shouldOpen);
    syncMobileNavbarState();
  });

  document.addEventListener("click", (event) => {
    if (!isMobile() || !event.target.closest(".navbar__menu-btn")) return;

    dropdownWrappers.forEach((element) => element.classList.remove("is-active"));
    navbar.classList.remove("is-active");
  });

  const syncResponsiveMode = () => {
    const isNowMobile = isMobile();

    if (isNowMobile === lastMobileState) {
      if (!isNowMobile && currentDropdown) {
        setOverlayHeight(getDropdownHeight(currentDropdown));
      }
      if (isNowMobile) syncMobileNavbarState();
      return;
    }

    lastMobileState = isNowMobile;
    clearTimers();
    dropdownWrappers.forEach((dropdownWrapper) => dropdownWrapper.classList.remove("is-open"));
    currentDropdown = null;
    setBackdropActive(false);
    setOverlayHeight(0);

    if (isNowMobile) {
      syncMobileNavbarState();
    } else {
      dropdownWrappers.forEach((dropdownWrapper) => dropdownWrapper.classList.remove("is-active"));
      navbar.classList.remove("is-active");
    }
  };

  mobileQuery.addEventListener("change", syncResponsiveMode);
  window.addEventListener("resize", () => {
    if (!isMobile() && currentDropdown) {
      setOverlayHeight(getDropdownHeight(currentDropdown));
    }
  });
  syncResponsiveMode();
}

export function initNavbarTheme() {
  const navbar = document.querySelector(".navbar");
  const themedSections = document.querySelectorAll("[data-nav-theme]");
  if (!navbar || !themedSections.length) return;

  const mobileThemeQuery = getMediaQuery(`(max-width: ${MOBILE_NAV_THEME_BREAKPOINT}px)`);
  let currentTheme = null;
  let updateScheduled = false;

  const updateNavbarTheme = () => {
    const scrollPosition = window.scrollY;
    const mobileLeadOffset = mobileThemeQuery.matches
      ? window.innerHeight * MOBILE_NAV_THEME_LEAD_RATIO
      : 0;
    const themeTriggerOffset = navbar.offsetHeight * NAV_THEME_TRIGGER_RATIO;
    let activeTheme = null;

    themedSections.forEach((section) => {
      const sectionBounds = section.getBoundingClientRect();
      const sectionTop = scrollPosition + sectionBounds.top;
      const sectionBottom = sectionTop + sectionBounds.height;

      if (
        scrollPosition + themeTriggerOffset + mobileLeadOffset >= sectionTop &&
        scrollPosition < sectionBottom
      ) {
        activeTheme = section.getAttribute("data-nav-theme");
      }
    });

    if (activeTheme === currentTheme) return;
    if (currentTheme) navbar.classList.remove(`is-${currentTheme}`);
    if (activeTheme) navbar.classList.add(`is-${activeTheme}`);
    currentTheme = activeTheme;
  };

  window.addEventListener(
    "scroll",
    () => {
      if (updateScheduled) return;

      updateScheduled = true;
      window.requestAnimationFrame(() => {
        updateNavbarTheme();
        updateScheduled = false;
      });
    },
    { passive: true }
  );
  updateNavbarTheme();
}

export function initNavbarLocalisation() {
  const gsapInstance = window.gsap;
  if (!gsapInstance) return;

  document.querySelectorAll(".navbar__menu-secondary").forEach((menuWrapper) => {
    const triggerWrapper = menuWrapper.querySelector(".navbar__localisation--trigger");
    const trigger = triggerWrapper?.querySelector("a") || triggerWrapper;
    const panel = menuWrapper.querySelector(".navbar__localisation-col");
    const closeButton = panel?.querySelector(".navbar__localisation--close");
    const helpColumns = Array.from(menuWrapper.querySelectorAll(".navbar__menu-col--help"));
    if (!trigger || !panel || !closeButton) return;

    let isOpen = false;
    let timeline;

    const buildTimeline = () => {
      const collapsedWidth = menuWrapper.getBoundingClientRect().width;

      gsapInstance.set(menuWrapper, { width: collapsedWidth });
      gsapInstance.set(helpColumns, { display: "", autoAlpha: 1 });
      gsapInstance.set(panel, { display: "none", xPercent: 110, autoAlpha: 0 });

      const nextTimeline = gsapInstance.timeline({
        paused: true,
        defaults: { ease: "power2.out" },
      });

      nextTimeline
        .set(panel, { display: LOCALISATION_PANEL_DISPLAY }, 0)
        .to(helpColumns, { autoAlpha: 0, duration: 0.22 }, 0)
        .set(helpColumns, { display: "none" }, 0.22)
        .to(menuWrapper, { width: "auto", duration: 0.38 }, 0.22)
        .to(panel, { xPercent: 0, autoAlpha: 1, duration: 0.38 }, 0.22);

      nextTimeline.eventCallback("onReverseComplete", () => {
        gsapInstance.set(panel, { display: "none", xPercent: 110, autoAlpha: 0 });
        gsapInstance.set(helpColumns, { display: "", autoAlpha: 1 });
        menuWrapper.style.width = "";
      });

      return nextTimeline;
    };

    const openPanel = () => {
      if (isOpen) return;

      isOpen = true;
      timeline?.kill();
      timeline = buildTimeline();
      timeline.play(0);
    };

    const closePanel = () => {
      if (!isOpen) return;

      isOpen = false;
      timeline?.reverse();
    };

    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      if (isOpen) closePanel();
      else openPanel();
    });

    closeButton.addEventListener("click", (event) => {
      event.preventDefault();
      closePanel();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePanel();
    });

    window.addEventListener("resize", () => {
      if (!isOpen) return;

      timeline?.kill();
      timeline = buildTimeline();
      timeline.progress(1);
    });
  });
}

export function initNavbarSearch(navigate = (url) => window.location.assign(url)) {
  const gsapInstance = window.gsap;
  if (!gsapInstance) return;

  const triggers = Array.from(document.querySelectorAll(".navbar__search--trigger"));
  const panel = document.querySelector(".navbar__search-wrap");
  const closeButton = panel?.querySelector(".navbar__search-close");
  const searchInput = panel?.querySelector('input[type="search"]');

  const navbar = panel?.closest(".navbar");
  const navbarContent = navbar?.querySelector(".navbar__mobile--additional-wrap");
  const resultsPanel = navbar?.querySelector(".search__results-nav");

  // Home is .navbar__menu-col--mid, so Webflow's standard mobile nav keeps the input inside
  // the menu flow. Desktop needs it in the bar instead, and CSS cannot get it there:
  // nav.navbar__menu--utility is `display: contents` (no box, so no containing block), and
  // making .navbar__mobile--additional-wrap relative drags the dropdowns 181px sideways.
  // Moving it out is also what lets the whole wrap be hidden again, since the input is no
  // longer inside it.
  const homeParent = panel?.parentElement;

  if (!triggers.length || !panel || !closeButton || !searchInput || !navbarContent || !homeParent) {
    return;
  }

  // Search splits at 767, not at the 991 nav breakpoint. On tablet the menu is behind the
  // hamburger (nav.navbar__menu--utility is display:none), but the bar is still visible — so
  // moving the input out of the closed menu is what makes it usable. Tablet takes the same
  // path as desktop, only into a different container.
  const phoneQuery = getMediaQuery(`(max-width: ${NAVBAR_SEARCH_PHONE_BREAKPOINT}px)`);
  const tabletQuery = getMediaQuery(
    `(min-width: ${NAVBAR_SEARCH_PHONE_BREAKPOINT + 1}px) and ` +
      `(max-width: ${NAVBAR_DROPDOWN_SETTINGS.mobileBreakpoint}px)`
  );
  const isPhone = () => phoneQuery.matches;
  const isTablet = () => tabletQuery.matches;

  // .navbar__tablet--additional-help is `display: contents` outside the tablet range, so it
  // only forms a box between 768 and 991. There the input belongs inside it and the results
  // panel directly after it; elsewhere the panel goes next to .navbar__mobile--additional-wrap.
  const tabletHelp = navbar?.querySelector(".navbar__tablet--additional-help");
  const menuButton = navbar?.querySelector(".navbar__menu-button");

  // On tablet the hamburger sits beside the input inside .navbar__tablet--additional-help,
  // so it has to fade with the rest of the nav rather than stay next to an open search.
  const hiddenContent = () =>
    isTablet() && menuButton ? [navbarContent, menuButton] : navbarContent;

  const moveToBar = () => {
    if (isTablet() && tabletHelp) {
      if (panel !== tabletHelp.firstElementChild) tabletHelp.prepend(panel);
      if (resultsPanel && resultsPanel.previousElementSibling !== tabletHelp) {
        tabletHelp.after(resultsPanel);
      }
      return;
    }

    if (panel.previousElementSibling === navbarContent) return;
    if (resultsPanel) navbarContent.after(panel, resultsPanel);
    else navbarContent.after(panel);
  };

  const moveHome = () => {
    if (panel.parentElement === homeParent) return;
    if (resultsPanel) homeParent.prepend(panel, resultsPanel);
    else homeParent.prepend(panel);
  };

  // Autofocus everywhere the panel slides in. On a phone it would raise the on-screen
  // keyboard over the results the user is trying to read.
  const shouldAutofocus = () => !isPhone();

  let isOpen = false;
  let timeline;
  let pendingFocus = false;

  const buildTimeline = () => {
    const hidden = hiddenContent();

    gsapInstance.set(hidden, { display: "", autoAlpha: 1 });
    gsapInstance.set(panel, { display: "none", xPercent: 110, autoAlpha: 0 });

    const nextTimeline = gsapInstance.timeline({
      paused: true,
      defaults: { ease: "power2.out" },
    });

    nextTimeline
      .to(hidden, { autoAlpha: 0, duration: 0.22 }, 0)
      .set(hidden, { display: "none" }, 0.22)
      .set(panel, { display: "flex" }, 0.22)
      .to(panel, { xPercent: 0, autoAlpha: 1, duration: 0.38 }, 0.22)
      // Focus only once the panel is displayed — focusing a display:none input is a no-op.
      // The flag keeps a resize-driven progress(1) from stealing focus back.
      .call(
        () => {
          if (!pendingFocus) return;
          pendingFocus = false;
          searchInput.focus();
        },
        null,
        0.24
      );

    nextTimeline.eventCallback("onReverseComplete", () => {
      gsapInstance.set(panel, { display: "none", xPercent: 110, autoAlpha: 0 });
      gsapInstance.set(hidden, { display: "", autoAlpha: 1 });
      moveHome();
    });

    return nextTimeline;
  };

  const openPanel = () => {
    if (isOpen) return;

    isOpen = true;

    // On mobile the input sits in the open menu permanently, styled by CSS. Building the
    // timeline there would write inline display/transform onto an always-visible element.
    if (isPhone()) {
      if (shouldAutofocus()) searchInput.focus();
      return;
    }

    // Move before the timeline is built, so it measures the panel where it will animate,
    // and before any focus, so re-parenting cannot blur the input.
    moveToBar();
    pendingFocus = shouldAutofocus();
    timeline?.kill();
    timeline = buildTimeline();
    timeline.play(0);
  };

  const closePanel = () => {
    if (!isOpen) return;

    isOpen = false;
    if (isPhone()) return;

    timeline?.reverse();
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      if (isOpen) closePanel();
      else openPanel();
    });
  });

  closeButton.addEventListener("click", (event) => {
    event.preventDefault();
    closePanel();
  });

  // Clicking outside closes the whole search, not just the results. navbar-search.js listens
  // for the same click to clear the input and the panel, so both halves close together.
  navbar?.querySelector(".navbar__overlay-close")?.addEventListener("click", () => {
    if (isPhone()) return;
    closePanel();
  });

  // Some browsers fire both `keydown` Enter and the `search` event for one key press.
  // Keying the guard to the query rather than a boolean means a repeat of the same press is
  // ignored while any different query still submits — no re-arming needed, so it cannot
  // get stuck if an expected `input` event never arrives.
  let lastSubmitted = null;

  const submitSearch = (event) => {
    const query = searchInput.value.trim();
    if (!query || query === lastSubmitted) return;

    lastSubmitted = query;
    event.preventDefault();
    navigate(`/search?${new URLSearchParams({ q: query })}`);
  };

  searchInput.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    submitSearch(event);
  });

  // The input is not inside a form, so a soft keyboard's Search/Go key has no submit to
  // trigger — on input[type=search] it fires this instead. Also fires on the native clear
  // button, where the empty-query check above makes it a no-op.
  searchInput.addEventListener("search", submitSearch);

  // Labels the mobile keyboard's action key as "Search" rather than "Go" or a newline.
  if (!searchInput.hasAttribute("enterkeyhint")) {
    searchInput.setAttribute("enterkeyhint", "search");
  }

  // Same contract as Enter: an empty query does nothing and leaves the event alone.
  navbar?.querySelector(".search__btn")?.addEventListener("click", submitSearch);

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closePanel();
  });

  window.addEventListener("resize", () => {
    if (!isOpen || isPhone()) return;

    timeline?.kill();
    timeline = buildTimeline();
    timeline.progress(1);
  });
}

initNavbarTheme();
runWhenDocumentReady(() => {
  initDeferredNavbarImages();
  initNavbarDropdowns();
  initNavbarLocalisation();
  initNavbarSearch();
});
